/**
 * TensionCurve — the story's shape: scene-by-scene dramatic tension (1-5)
 * plotted as a smooth line in the current card order. Reshapes live as cards
 * are dragged / undone / committed. Dots are tinted by scene sentiment; click
 * a dot to jump to its card.
 *
 * Extras: drag the bottom edge to grow the strip; drop ACT BREAK markers on
 * the curve (anchored to the scene they follow, so they ride along when cards
 * reorder). Click a marker chip to rename/remove it, drag it to move it.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trash2 } from "lucide-react";

const TONE_COLORS = { negative: "#c4705f", positive: "#7a9a62", neutral: "#c9b45e" };
const PAD_X = 20;
const PAD_TOP = 16;
const PAD_BOT = 22;
export const CURVE_MIN_H = 96;
export const CURVE_MAX_H = 380;

function smoothPath(pts) {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    d += ` C ${p1.x + (p2.x - p0.x) / 6} ${p1.y + (p2.y - p0.y) / 6}, ${p2.x - (p3.x - p1.x) / 6} ${p2.y - (p3.y - p1.y) / 6}, ${p2.x} ${p2.y}`;
  }
  return d;
}

export function TensionCurve({ items, onDotClick, height = CURVE_MIN_H, onHeightChange, markers = [], onMarkersChange, placing = false, onPlacingChange, readOnly }) {
  const H = Math.min(CURVE_MAX_H, Math.max(CURVE_MIN_H, height));
  const boxRef = useRef(null);
  const editInputRef = useRef(null);
  const markerDrag = useRef(null);
  const [w, setW] = useState(0);
  const [editing, setEditing] = useState(null);
  const [dragGap, setDragGap] = useState(null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver((es) => setW(es[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const step = items.length > 1 ? (w - PAD_X * 2) / (items.length - 1) : 0;
  const xs = useMemo(() => items.map((_, i) => PAD_X + i * step), [items, step]);

  const pts = useMemo(() => {
    if (!w || !items.length) return [];
    return items
      .map((it, i) => ({
        ...it,
        x: xs[i],
        y: it.tension != null ? PAD_TOP + ((5 - it.tension) * (H - PAD_TOP - PAD_BOT)) / 4 : null,
      }))
      .filter((p) => p.y != null);
  }, [items, xs, w, H]);

  const gapX = (g) => {
    if (!items.length || !xs.length) return 0;
    if (g >= items.length) return xs[items.length - 1] + 10;
    if (g <= 0) return xs[0] - 10;
    return (xs[g - 1] + xs[g]) / 2;
  };

  const nearestGap = (clientX) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return 1;
    const x = clientX - rect.left;
    let best = 1, bd = Infinity;
    for (let g = 1; g < items.length; g++) {
      const d = Math.abs(x - gapX(g));
      if (d < bd) { bd = d; best = g; }
    }
    return best;
  };

  const resolved = useMemo(() => markers
    .map((m) => {
      const idx = items.findIndex((it) => it.sceneId === m.after_scene_id);
      return idx < 0 ? null : { ...m, gap: idx + 1 };
    })
    .filter(Boolean), [markers, items]);

  // -------- act break placement --------
  useEffect(() => {
    if (!placing) return;
    const fn = (e) => { if (e.key === "Escape") onPlacingChange?.(false); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [placing, onPlacingChange]);

  const placeAt = (e) => {
    if (items.length < 2) { onPlacingChange?.(false); return; }
    const g = nearestGap(e.clientX);
    const label = `Act ${["II", "III", "IV", "V", "VI", "VII"][markers.length] || markers.length + 2}`;
    onMarkersChange?.([...markers, {
      id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      after_scene_id: items[g - 1].sceneId,
      label,
    }]);
    onPlacingChange?.(false);
  };

  // -------- marker drag / click-to-edit --------
  const onMarkerPointerDown = (e, m) => {
    if (readOnly || !onMarkersChange) return;
    e.stopPropagation();
    e.preventDefault();
    markerDrag.current = { id: m.id, x0: e.clientX, moved: false };
    const move = (ev) => {
      const d = markerDrag.current;
      if (!d) return;
      if (!d.moved && Math.abs(ev.clientX - d.x0) < 5) return;
      d.moved = true;
      setDragGap({ id: d.id, gap: nearestGap(ev.clientX) });
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const d = markerDrag.current;
      markerDrag.current = null;
      if (!d) return;
      if (d.moved) {
        const g = nearestGap(ev.clientX);
        onMarkersChange(markers.map((mk) => (mk.id === d.id ? { ...mk, after_scene_id: items[g - 1].sceneId } : mk)));
        setDragGap(null);
      } else {
        setEditing(d.id);
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const saveLabel = () => {
    const v = (editInputRef.current?.value || "").trim();
    if (v) onMarkersChange?.(markers.map((mk) => (mk.id === editing ? { ...mk, label: v.slice(0, 40) } : mk)));
    setEditing(null);
  };

  // -------- height resize --------
  const onResizeDown = (e) => {
    if (!onHeightChange) return;
    e.preventDefault();
    const y0 = e.clientY, h0 = H;
    const move = (ev) => onHeightChange(Math.min(CURVE_MAX_H, Math.max(CURVE_MIN_H, h0 + ev.clientY - y0)));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const showNumbers = step >= 22;
  const editingMarker = editing ? resolved.find((m) => m.id === editing) : null;

  return (
    <div ref={boxRef} className="relative shrink-0 border-b border-[var(--line)] bg-[var(--paper)] select-none" style={{ height: H }} data-testid="tension-curve">
      <span className="absolute left-1.5 font-sans text-[8px] uppercase tracking-widest text-[var(--ink-muted)]" style={{ top: PAD_TOP - 8 }}>High</span>
      <span className="absolute left-1.5 font-sans text-[8px] uppercase tracking-widest text-[var(--ink-muted)]" style={{ bottom: PAD_BOT - 10 }}>Low</span>

      {pts.length === 0 ? (
        <span className="absolute inset-0 flex items-center justify-center font-sans text-[11px] text-[var(--ink-muted)]" data-testid="tension-curve-pending">
          Reading tension scene by scene…
        </span>
      ) : (
        <svg width="100%" height={H} className="block">
          {[1, 3, 5].map((t) => {
            const y = PAD_TOP + ((5 - t) * (H - PAD_TOP - PAD_BOT)) / 4;
            return <line key={t} x1={PAD_X} y1={y} x2={Math.max(PAD_X, w - PAD_X)} y2={y} stroke="var(--line)" strokeWidth="1" strokeDasharray="2 5" />;
          })}
          {pts.length > 1 && (
            <>
              <path d={`${smoothPath(pts)} L ${pts[pts.length - 1].x} ${H - PAD_BOT + 8} L ${pts[0].x} ${H - PAD_BOT + 8} Z`} fill="rgba(211,110,90,0.10)" stroke="none" />
              <path d={smoothPath(pts)} fill="none" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" />
            </>
          )}
          {resolved.map((m, i) => {
            const g = dragGap?.id === m.id ? dragGap.gap : m.gap;
            const x = gapX(g);
            return <line key={m.id} x1={x} y1={20} x2={x} y2={H - PAD_BOT + 8} stroke="#6b5d4c" strokeWidth="1.5" strokeDasharray="6 4" data-testid={`act-marker-line-${i}`} />;
          })}
          {pts.map((p) => (
            <g key={p.sceneId} className="cursor-pointer" onClick={() => onDotClick?.(p.sceneId)} data-testid={`tension-dot-${p.number}`}>
              <circle cx={p.x} cy={p.y} r="4.5" fill={TONE_COLORS[p.sentiment] || "#b8ad9c"} stroke="#fff" strokeWidth="1.5" />
              <circle cx={p.x} cy={p.y} r="10" fill="transparent" />
              <title>{`Sc ${p.number} — ${p.heading}\nTension ${p.tension}/5`}</title>
              {showNumbers && (
                <text x={p.x} y={H - PAD_BOT + 14} textAnchor="middle" fontSize="8" fill="#8a8171" className="select-none font-sans">{p.number}</text>
              )}
            </g>
          ))}
        </svg>
      )}

      {/* marker chips */}
      {resolved.map((m) => {
        const g = dragGap?.id === m.id ? dragGap.gap : m.gap;
        const x = gapX(g);
        return (
          <button
            key={m.id}
            onPointerDown={(e) => onMarkerPointerDown(e, m)}
            className={`absolute top-1 -translate-x-1/2 font-sans text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#4a4238] text-white shadow whitespace-nowrap ${readOnly || !onMarkersChange ? "cursor-default" : "cursor-ew-resize hover:bg-[#5d5346]"}`}
            style={{ left: Math.min(Math.max(x, 30), Math.max(30, w - 30)) }}
            title={readOnly || !onMarkersChange ? m.label : `${m.label} — drag to move · click to rename or remove`}
            data-testid={`act-marker-chip-${m.id}`}
          >
            {m.label}
          </button>
        );
      })}

      {placing && <div className="absolute inset-0 z-10 cursor-crosshair" onClick={placeAt} data-testid="curve-place-overlay" />}

      {/* marker rename/remove popover */}
      {editingMarker && (
        <div
          className="absolute z-30 flex items-center gap-1.5 bg-[var(--paper)] border border-[var(--line)] rounded-lg shadow-xl p-1.5"
          style={{ left: Math.min(Math.max(gapX(editingMarker.gap), 90), Math.max(90, w - 130)), top: 24, transform: "translateX(-50%)" }}
          data-testid="marker-edit-popover"
        >
          <input
            ref={editInputRef}
            defaultValue={editingMarker.label}
            maxLength={40}
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveLabel();
              if (e.key === "Escape") setEditing(null);
            }}
            className="w-28 text-xs font-sans border border-[var(--line)] rounded px-2 py-1 outline-none focus:border-[var(--brand)] bg-[var(--canvas)]"
            data-testid="marker-edit-input"
          />
          <button onClick={saveLabel} className="text-[10px] px-2 py-1 rounded btn-brand" data-testid="marker-edit-save">Save</button>
          <button
            onClick={() => { onMarkersChange?.(markers.filter((mk) => mk.id !== editing)); setEditing(null); }}
            className="p-1 rounded text-[var(--ink-muted)] hover:text-[var(--err)]"
            title="Remove this act break"
            data-testid="marker-edit-remove"
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}

      {/* height resize handle */}
      {onHeightChange && (
        <div onPointerDown={onResizeDown} className="absolute bottom-0 inset-x-0 h-2 cursor-ns-resize group z-20" title="Drag to resize the curve" data-testid="tension-curve-resize">
          <div className="absolute bottom-0 inset-x-0 h-[3px] transition-colors group-hover:bg-[rgba(211,110,90,0.45)]" />
        </div>
      )}
    </div>
  );
}
